import type { RoutingRule } from "./types";

// One key/value pair from the rule's TagPredicates. The key is bound
// to a dropdown over the backend's tag-key registry; the value stays
// free-form because tag *values* are unconstrained (customer IDs,
// group codes, etc.).
export interface PredicatePair {
    readonly id: string;
    readonly key: string;
    readonly value: string;
}

// Form shape shared by create and edit. Workflow kind is captured in
// the form for both flows but is only writable on create — the edit
// path renders the select disabled, because the kind names the rule's
// storage partition and changing it would be a semantic move, not an
// edit. TaskType is stored as the empty string when "any task type"
// (the wildcard); the payload converter normalizes it back to null at
// the wire boundary so the backend's "null = wildcard" contract stays
// canonical.
export interface RuleFormState {
    readonly workflowKind: string;
    readonly entityType: string;
    readonly taskType: string;
    readonly description: string;
    readonly predicates: PredicatePair[];
    readonly targetQueueId: string;
}

export function emptyForm(): RuleFormState {
    return {
        workflowKind: "",
        entityType: "",
        taskType: "",
        description: "",
        predicates: [],
        targetQueueId: "",
    };
}

export function fromRule(rule: RoutingRule): RuleFormState {
    return {
        workflowKind: rule.workflowKind,
        entityType: rule.entityType ?? "",
        taskType: rule.taskType ?? "",
        description: rule.description,
        predicates: Object.entries(rule.tagPredicates).map(([key, value]) => ({
            id: crypto.randomUUID(),
            key,
            value,
        })),
        targetQueueId: rule.targetQueueId,
    };
}

export function toPayload(form: RuleFormState): {
    workflowKind: string;
    entityType: string | null;
    taskType: string | null;
    description: string;
    tagPredicates: Record<string, string>;
    targetQueueId: string;
} {
    const predicates: Record<string, string> = {};
    for (const p of form.predicates) {
        if (p.key.length === 0) continue;
        predicates[p.key] = p.value;
    }
    return {
        workflowKind: form.workflowKind.trim(),
        entityType: form.entityType.trim().length > 0 ? form.entityType.trim() : null,
        taskType: form.taskType.trim().length > 0 ? form.taskType.trim() : null,
        description: form.description.trim(),
        tagPredicates: predicates,
        targetQueueId: form.targetQueueId,
    };
}
